<template>
  <div class="env-select">
    <TagEnvSelect
      :visible.sync="visible"
      :env-list="showEnvList"
      :theme="theme"
      :env="envListMap[currentEnv]"
      @change="handleChange($event.env, $event.id)"
    >
      <span :class="`env-select_button ${visible ? 'env-select_button-active' : ''}`">
        <span>{{ envName }}</span>
        <Loader2
          v-if="loading"
          class="env-select_icon env-select_icon-loading"
          :size="14"
        />
        <ChevronDown
          v-else
          class="env-select_icon"
          :size="14"
        />
      </span>
    </TagEnvSelect>
  </div>
</template>

<script>
import xss from 'xss';
import qs from 'query-string';
import { computed, onMounted, toRefs } from '@vue/composition-api';
import { useStore } from '@store/index';
import { mapState, mapActions } from 'vuex';
import { I18N_BASE_APP_PATH_REGEX, I18N_BASE_BRANCH_APP_PATH_REGEX, LANG_PREFIX } from '@config/constant';
import { isDefaultBaseAppPath, isBaseBranchAppPath, getPathCompiler, getPathMatcher } from '@utils/path';
import { executeSwitchHook } from '@utils/executeSwitchHook';
import { buildWildcardHost, getRuntimeWildcardDomain, parseWildcardHost } from '@utils/wildcardHost';
import { useHostConfig } from '@pages/project/projectConfig/hostConfig/useHostConfig';
import logger from '@utils/logger';
import { useRouter } from '@/router/useRouter';
import { ChevronDown, Loader2 } from 'lucide-vue';
import { TagEnvSelect } from './TagEnvSelect';

export default {
  name: 'NavEnvSelect',
  components: { ChevronDown, Loader2, TagEnvSelect },
  inject: {
    ctx: 'ctx',
  },
  props: {
    mode: {
      type: String,
      default: 'editor',
    },
    theme: {
      type: String,
      default: 'light',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showList: {
      type: Array,
      default: () => ['*'],
    },
    redirect: {
      type: String,
      default: 'same',
    },
  },
  setup(props) {
    const { mode } = toRefs(props);
    const store = useStore();
    const { route } = useRouter();

    // 运行时从 vuex 取; 编辑时从 query 取
    const projectId = computed(() => store.state.app.projectId || route.value?.query?.projectid);

    const { hostConfigs, getHostConfigs } = useHostConfig(projectId);

    onMounted(() => {
      getHostConfigs({ tips: false, isRuntime: mode.value === 'runtime' });
    });

    return { projectId, hostConfigs };
  },
  data() {
    return {
      visible: false,
    };
  },
  computed: {
    ...mapState({
      currentEnv: state => state.app.env,
      projectInfo: state => state.runtime.project,
      envList: state => state.env.envList,
      loading: state => state.loading.fetchEnvList,
    }),
    envListMap() {
      const map = {};
      this.envList.forEach((item) => {
        map[item.env] = item;
      });
      return map;
    },
    envName() {
      return this.envListMap[this.currentEnv]?.name || this.currentEnv;
    },
    showEnvList() {
      if (this.showList.includes('*')) {
        return this.envList;
      }
      return this.envList.filter(item => this.showList.includes(item.id));
    },
  },
  created() {
    this.fetchEnvList({ projectId: this.projectId });
  },
  methods: {
    ...mapActions(['fetchEnvList']),
    /**
     * 切换运行时环境。
     *
     * 分支按以下优先级匹配，命中即短路：
     *   1. 泛域名（runtimeWildcardDomain）：当前 host 形如 `{projectId}[-{env}].{root}`
     *      时，按相同规则反向拼接目标 env 的 host，固定使用 `/page` 作为 pathPrefix。
     *      与服务端 getGroupFromPath 行为一致，命中即不再读取 hostConfigs。
     *   2. 默认长路径 / 分支路径（/xy/app/:env/:projectId 或 /xy/app/branch/...）：
     *      若同 host 上存在目标 env 的 hostConfig，转为「长路径 → 短路径」；否则保持长路径。
     *   3. 自定义短路径（已经在某条 hostConfig 的 pathPrefix 之下）：
     *      若目标 env 同样有 hostConfig，转为「短路径 → 短路径」；否则兜底回长路径。
     *
     * @param {string} env 目标环境标识
     */
    handleChange(env) {
      this.visible = false;
      // 动画更自然
      setTimeout(async () => {
        if (this.disabled) return false;
        // compiler/matcher/rootMatcher 由下方各分支按当前形态赋值，最终交给 redirectByMatcher 使用
        let compiler;
        let matcher;
        let rootMatcher;

        const isDefault = isDefaultBaseAppPath(); // 是否默认的运行时路径
        const isBranch = isBaseBranchAppPath(); // 是否分支路径

        const { protocol } = location;
        let { host } = location;

        /**
         * 各分支共享的「跳转尾段」：
         *   - 用 matcher / rootMatcher 解析当前 path，得到剩余 page 片段
         *   - 根据 redirect=home 或 same 确定保留 query/page 行为
         *   - 调用 compiler 拼出目标 env 的 base，再附加 path
         *   - 兼容遗留的 envConfig.hook（允许业务覆写最终 path）
         *   - 经 xss 处理后整页跳转
         *
         * @returns {boolean} 是否完成跳转（matcher 未命中时返回 false，让外层走兜底分支）
         */
        const redirectByMatcher = async ({ targetHost }) => {
          const matchRes = matcher(location.pathname) || rootMatcher(location.pathname);
          logger.info('[env-switch].matchRes', matchRes);
          if (!matchRes) return false;

          const { params } = matchRes;
          const paramsPage = Array.isArray(params?.page) ? params.page.join('/') : params.page;
          let path = '';
          if (this.redirect === 'home') {
            // 跳转回应用首页，保留当前 query
            const resolvedRoute = this.$router.resolve({ path: '/' });
            const resolvePath = resolvedRoute?.resolved?.path || '/';
            const resolveQuery = resolvedRoute?.resolved?.query ?? {};
            const currentQuery = this.$route.query ?? {};
            path = qs.stringifyUrl({ url: resolvePath, query: { ...resolveQuery, ...currentQuery } });
          } else {
            // 保留当前页面相对路径与 search
            path = paramsPage ? `/${paramsPage}${location.search}` : location.search;
          }
          const prefix = compiler({ env, projectId: this.projectId }, { pretty: true });
          let target = `${protocol}//${targetHost}${prefix}`;

          // FIXME: 废弃的环境切换钩子函数（保留兼容存量项目自定义跳转路径）
          const navs = this.projectInfo?.uiInfo.navlink?.defaultNav;
          const envConfig = navs?.find(i => i.id === 'env');
          if (envConfig?.hook) {
            const hookPath = await executeSwitchHook(
              envConfig.hook,
              { ctx: this.ctx, from: this.currentEnv, to: env, switchingKey: 'env' },
            );
            if (hookPath) {
              path = hookPath;
              logger.info('[env-switch].hook.path', path);
            }
          }
          target += path;
          logger.info('[env-switch].target', target);
          location.href = xss(target);
          return true;
        };

        // ===== 分支 1：泛域名（runtimeWildcardDomain）=====
        // 与服务端 parseWildcardHost 规则保持一致：未配置或当前 host 不命中时回退到旧分支
        const wildcardDomain = getRuntimeWildcardDomain();
        const currentWild = wildcardDomain && parseWildcardHost(location.host, wildcardDomain);
        if (currentWild) {
          // 透传当前端口，保证本地/灰度环境如 `proj1.wu.woa.com:3000` 切换后仍带端口
          const wildPort = location.host.includes(':') ? location.host.split(':')[1] : '';
          const wildcardHost = buildWildcardHost({
            projectId: this.projectId,
            env,
            wildcardDomain,
            port: wildPort,
          });
          if (wildcardHost) {
            // 泛域名命中场景下 pathPrefix 固定为 `/page`，对齐服务端 wildcardHost.js 的返回值，
            // 避免被自定义 hostConfig 的 pathPrefix 干扰（服务端命中后也不会再查 host_config）
            const wildcardPrefix = `${LANG_PREFIX}${currentWild.pathPrefix}`;
            compiler = getPathCompiler(wildcardPrefix);
            matcher = getPathMatcher(`${wildcardPrefix}/:page*`, { end: false });
            rootMatcher = getPathMatcher(wildcardPrefix, { end: false });
            const wildcardHandled = await redirectByMatcher({ targetHost: wildcardHost });
            // 已成功处理则短路；未匹配到 path 时 fallthrough 到下方常规分支
            if (wildcardHandled) return;
          }
        }

        // ===== 分支 2/3：默认长路径 / 分支路径 / 自定义短路径 =====
        // 目标 env 的 hostConfig（用于「换 host + pathPrefix」短路径）
        const hostConfig = this.hostConfigs.find(item => item.envId.replace(`${item.projectId}:`, '') === env);
        const targetPrefix = `${LANG_PREFIX}${hostConfig?.pathPrefix ?? '/page'}`;
        if (isDefault || isBranch) {
          // 如果当前 host 不在自定义域名配置中，则使用默认的长路径
          const canUseHostConfig = this.hostConfigs.some(item => item.host === host);
          const matcherPathPrefix = isBranch ? I18N_BASE_BRANCH_APP_PATH_REGEX : I18N_BASE_APP_PATH_REGEX;
          // 普通链接
          if (canUseHostConfig && hostConfig) {
            // 长路径 → 短路径
            host = hostConfig.host;
            compiler = getPathCompiler(targetPrefix);
          } else {
            // 长路径 → 长路径
            compiler = getPathCompiler(I18N_BASE_APP_PATH_REGEX);
          }
          matcher = getPathMatcher(`${matcherPathPrefix}/:page*`, { end: false });
          rootMatcher = getPathMatcher(matcherPathPrefix, { end: false });
        } else {
          // 自定义路径
          // 当前 env 的 hostConfig（用来反推「我现在的短路径形态」）
          const currentConfig = this.hostConfigs.find(item => item.envId.replace(`${item.projectId}:`, '') === this.currentEnv);
          const currentPrefix = `${LANG_PREFIX}${currentConfig?.pathPrefix ?? '/page'}`;

          if (hostConfig && currentConfig) {
            // 短路径 → 短路径
            // 有对应环境的自定义路径配置
            host = hostConfig.host;
            compiler = getPathCompiler(targetPrefix);
          } else {
            // 短路径 → 长路径
            // 没有对应环境的自定义路径配置, 兜底使用长路径
            compiler = getPathCompiler(I18N_BASE_APP_PATH_REGEX);
          }
          matcher = getPathMatcher(`${currentPrefix}/:page*`, { end: false });
          rootMatcher = getPathMatcher(currentPrefix, { end: false });
        }

        // 兜底：matcher 未命中时静默返回（与历史行为一致，不强制跳转）
        await redirectByMatcher({ targetHost: host });
      }, 300);
    },
  },
};
</script>

<style lang="scss" scoped>
.env-select {
  margin: var(--xy-custom-nav-env-select-margin);
}

.env-select_button {
  height: 28px;
  border-radius: 14px;
  padding: 5px 0;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
}

.env-select_button:hover {
  color: var(--xy-custom-nav-hover-color);
}

.env-select_button-active {
  color: var(--xy-custom-nav-active-color);
}

.env-select_icon {
  flex-shrink: 0;
}

.env-select_icon-loading {
  animation: env-select-icon-spin 1s linear infinite;
}

.env-select_options {
  cursor: pointer;
}

@keyframes env-select-icon-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
